import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Collapse,
  CircularProgress,
  Button,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Grid,
  Card
} from '@mui/material';
import {
  Home as HomeIcon,
  Forum as ForumIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Book as BookIcon,
  EmojiEvents as SuccessIcon,
  Notifications as NoticeIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Quiz as QuizIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Lightbulb as LightbulbIcon,
  Grade as GradeIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const drawerWidth = 280;

const ClienLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, isEvaluator, logout, loading: authLoading } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [communityExpanded, setCommunityExpanded] = useState(true);
  const [homeExpanded, setHomeExpanded] = useState(true);
  const [learningExpanded, setLearningExpanded] = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // 로딩 타임아웃 설정 (10초)
  useEffect(() => {
    if (authLoading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000);
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [authLoading]);

  // 인증되지 않은 사용자 처리
  useEffect(() => {
    // 로딩이 완료되고 사용자가 없으며, 보호된 경로에 있는 경우
    if (!authLoading && !isLoggedIn && !location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup')) {
      // 공개 페이지가 아닌 경우 로그인 페이지로 리다이렉트
      const publicPaths = ['/', '/about', '/contact'];
      if (!publicPaths.includes(location.pathname)) {
        // 세션 만료로 인한 리다이렉트인지 확인
        const isSessionExpired = localStorage.getItem('session_expired') === 'true';
        if (isSessionExpired) {
          localStorage.removeItem('session_expired');
          navigate('/login?session_expired=true');
        } else {
          navigate('/login');
        }
      }
    }
  }, [authLoading, isLoggedIn, location.pathname, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    handleUserMenuClose();
    navigate('/');
  };

  const handleCommunityToggle = () => {
    setCommunityExpanded(!communityExpanded);
  };

  const handleHomeToggle = () => {
    setHomeExpanded(!homeExpanded);
  };

  const handleLearningToggle = () => {
    setLearningExpanded(!learningExpanded);
  };

  const handleAdminToggle = () => {
    setAdminExpanded(!adminExpanded);
  };

  // 메뉴 아이템 정의
  const menuItems = [
    {
      text: '홈',
      icon: <HomeIcon />,
      path: '/',
      divider: false,
      hasSubmenu: true,
      submenu: [
        {
          text: '메인 페이지',
          icon: <HomeIcon />,
          path: '/'
        },
        {
          text: '요금제',
          icon: <PaymentIcon />,
          path: '/pricing'
        }
      ]
    },
    {
      text: '수험장 (Practice)',
      icon: <QuizIcon />,
      path: '/practice',
      divider: false,
      hasSubmenu: true,
      submenu: [
        {
          text: '내 학습 분석',
          icon: <AssessmentIcon />,
          path: '/my-learning'
        },
        {
          text: 'MBE 실전 훈련',
          icon: <QuizIcon />,
          path: '/mbe_test'
        },
        {
          text: 'MEE 에세이 작성',
          icon: <ArticleIcon />,
          path: '/mee_test'
        },
        {
          text: 'MPT 기록형 훈련',
          icon: <SchoolIcon />,
          path: '/mpt_test'
        }
      ]
    },
    {
      text: '자료실 (Library)',
      icon: <BookIcon />,
      path: '/library',
      divider: false,
      hasSubmenu: true,
      submenu: [
        {
          text: 'BLL 핵심 요약',
          icon: <BookIcon />,
          path: '/bll-learning'
        },
        {
          text: 'Case Brief (판례 요약)',
          icon: <ArticleIcon />,
          path: '/community/casebrief'
        },
        {
          text: '판례 관계도 (Mind Maps)',
          icon: <SchoolIcon />,
          path: '/community/study-tips'
        },
        {
          text: 'MBE&MEE 이론 Study',
          icon: <LightbulbIcon />,
          path: '/community/mbe-study'
        },
        {
          text: '법률용어 사전',
          icon: <ForumIcon />,
          path: '/community/legal-dictionary'
        }
      ]
    },
    {
      text: '커뮤니티 (Community)',
      icon: <ForumIcon />,
      path: '/community',
      divider: false,
      hasSubmenu: true,
      submenu: [
        {
          text: '공지사항',
          icon: <NoticeIcon />,
          path: '/community/notices'
        },
        {
          text: '자유게시판',
          icon: <ForumIcon />,
          path: '/community/free-board'
        },
        {
          text: '합격수기',
          icon: <SuccessIcon />,
          path: '/community/success-stories'
        },
        {
          text: '스터디그룹',
          icon: <GroupIcon />,
          path: '/community/study-groups'
        }
      ]
    },
    // 관리자 전용 메뉴
    ...(isAdmin ? [{
      text: '관리자',
      icon: <PersonIcon />,
      path: '/admin',
      divider: false,
      hasSubmenu: true,
      adminOnly: true,
      submenu: [
        {
          text: '사용자 관리',
          icon: <PersonIcon />,
          path: '/user-management'
        },
        {
          text: 'MBE 관리',
          icon: <QuizIcon />,
          path: '/mbe_admin'
        },
        {
          text: '관리자 게시판',
          icon: <ForumIcon />,
          path: '/admin/logs'
        },
        {
          text: '개발 로드맵',
          icon: <ArticleIcon />,
          path: '/admin/roadmap'
        }
      ]
    }] : []),
    // 평가 메뉴 (평가자와 관리자 모두)
    ...(isEvaluator ? [{
      text: '평가',
      icon: <GradeIcon />,
      path: '/evaluator',
      divider: false,
      hasSubmenu: false,
      evaluatorOnly: true
    }] : [])
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // 로딩 상태 처리
  if (authLoading && !loadingTimeout) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          인증 정보 확인 중...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          잠시만 기다려주세요
        </Typography>
      </Box>
    );
  }

  // 로딩 타임아웃 처리
  if (authLoading && loadingTimeout) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        p: 3
      }}>
        <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
          연결에 문제가 발생했습니다
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
          인증 정보를 확인하는데 시간이 오래 걸리고 있습니다.<br />
          브라우저 캐시를 삭제하고 다시 시도해주세요.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              // 로컬 스토리지 정리
              if (typeof window !== 'undefined') {
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                  if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key);
                  }
                });
              }
              window.location.reload();
            }}
          >
            새로고침
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              navigate('/login');
            }}
          >
            로그인 페이지로
          </Button>
        </Box>
      </Box>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 로고 영역 */}
      <Box sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        }
      }}
        onClick={() => {
          navigate('/');
          if (isMobile) setMobileOpen(false);
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
          BLL Pro
        </Typography>
        {isLoggedIn && (
          <Chip
            label="Premium"
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
            }}
            variant="outlined"
          />
        )}
      </Box>

      {/* 메뉴 리스트 */}
      <List sx={{ flex: 1, pt: 1 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={item.text}>
            {/* 권한에 따른 메뉴 표시 */}
            {(!item.adminOnly || isAdmin) && (!item.evaluatorOnly || isEvaluator) && (
              <>
                {item.hasSubmenu ? (
                  // 서브메뉴가 있는 아이템
                  <>
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isActivePath(item.path)}
                        onClick={() => {
                          if (item.text === '홈') handleHomeToggle();
                          else if (item.text === '커뮤니티') handleCommunityToggle();
                          else if (item.text === '학습') handleLearningToggle();
                          else if (item.text === '관리자') handleAdminToggle();
                          else if (item.text === '평가') {
                            navigate('/evaluator');
                            if (isMobile) setMobileOpen(false);
                          }
                        }}
                        sx={{
                          mx: 1,
                          borderRadius: 1,
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                          },
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.9rem',
                            fontWeight: isActivePath(item.path) ? 600 : 400
                          }}
                        />
                        {(() => {
                          if (item.text === '홈') return homeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />;
                          if (item.text === '커뮤니티') return communityExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />;
                          if (item.text === '학습') return learningExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />;
                          if (item.text === '관리자') return adminExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />;
                          return null;
                        })()}
                      </ListItemButton>
                    </ListItem>
                    <Collapse
                      in={(() => {
                        if (item.text === '홈') return homeExpanded;
                        if (item.text === '커뮤니티') return communityExpanded;
                        if (item.text === '학습') return learningExpanded;
                        if (item.text === '관리자') return adminExpanded;
                        return false;
                      })()}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {item.submenu.map((subItem) => (
                          <ListItem key={subItem.text} disablePadding>
                            <ListItemButton
                              selected={location.pathname === subItem.path}
                              onClick={() => {
                                navigate(subItem.path);
                                if (isMobile) setMobileOpen(false);
                              }}
                              sx={{
                                pl: 4,
                                mx: 1,
                                borderRadius: 1,
                                color: 'white',
                                '&:hover': {
                                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: 'rgba(255, 255, 255, 0.4)',
                                  color: 'white',
                                  borderLeft: '4px solid #ffffff',
                                  fontWeight: 'bold',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.5)',
                                  },
                                  '& .MuiListItemIcon-root': {
                                    color: '#ffffff',
                                  },
                                  '& .MuiListItemText-primary': {
                                    fontWeight: 'bold',
                                    color: '#ffffff',
                                  },
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={subItem.text}
                                primaryTypographyProps={{
                                  fontSize: '0.85rem',
                                  fontWeight: location.pathname === subItem.path ? 700 : 400,
                                  color: location.pathname === subItem.path ? '#ffffff' : 'rgba(255, 255, 255, 0.9)'
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </>
                ) : (
                  // 일반 메뉴 아이템
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={isActivePath(item.path)}
                      onClick={() => {
                        navigate(item.path);
                        if (isMobile) setMobileOpen(false);
                      }}
                      sx={{
                        mx: 1,
                        borderRadius: 1,
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          bgcolor: 'rgba(255, 255, 255, 0.4)',
                          color: 'white',
                          borderLeft: '4px solid #ffffff',
                          fontWeight: 'bold',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '& .MuiListItemIcon-root': {
                            color: '#ffffff',
                          },
                          '& .MuiListItemText-primary': {
                            fontWeight: 'bold',
                            color: '#ffffff',
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: isActivePath(item.path) ? 700 : 400,
                          color: isActivePath(item.path) ? '#ffffff' : 'rgba(255, 255, 255, 0.9)'
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
                {item.divider && <Divider sx={{ my: 1 }} />}
              </>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* 사용자 정보 (하단) */}
      {isLoggedIn && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.8rem',
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white'
              }}
              onClick={handleUserMenuOpen}
            >
              {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', color: 'white' }}>
                {user?.user_metadata?.full_name || user?.email?.split('@')[0] || '사용자'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {user?.email}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  const getActiveMainMenu = () => {
    return menuItems.find(item => 
      isActivePath(item.path) || (item.submenu && item.submenu.some(sub => isActivePath(sub.path)))
    );
  };
  const activeMenu = getActiveMainMenu();

  const bottomNavValue = location.pathname.startsWith('/mbe_test') ? 1 : 
                         location.pathname.startsWith('/community') ? 2 : 
                         location.pathname.startsWith('/my-learning') ? 3 : 
                         mobileOpen && isMobile ? 4 : 0;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {/* 상단 네비게이션 (데스크톱 & 모바일) */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: '#ffffff',
          color: '#212121',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, lg: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* 로고 */}
            <Typography 
              variant="h5" 
              sx={{ fontWeight: 800, fontFamily: '"Playfair Display", serif', cursor: 'pointer', color: '#212121' }}
              onClick={() => navigate('/')}
            >
              BLL Pro
            </Typography>

            {/* PC 메인 메뉴 */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {menuItems.filter(i => (!i.adminOnly || isAdmin) && (!i.evaluatorOnly || isEvaluator)).map((item) => (
                  <Button
                    key={item.text}
                    onClick={() => {
                      if (item.submenu && item.submenu.length > 0) {
                        navigate(item.submenu[0].path);
                      } else {
                        navigate(item.path);
                      }
                    }}
                    sx={{
                      color: activeMenu?.text === item.text ? '#212121' : '#757575',
                      fontWeight: activeMenu?.text === item.text ? 700 : 500,
                      px: 2,
                      py: 1,
                      borderBottom: activeMenu?.text === item.text ? '2px solid #212121' : '2px solid transparent',
                      borderRadius: 0,
                      '&:hover': {
                        bgcolor: 'transparent',
                        color: '#212121'
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isLoggedIn ? (
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={handleUserMenuOpen}>
                 <Avatar sx={{ width: 32, height: 32, bgcolor: '#424242' }}>
                   {user?.user_metadata?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                 </Avatar>
               </Box>
            ) : (
               <Button variant="outlined" onClick={() => navigate('/login')} size="small" sx={{ borderColor: '#e0e0e0', color: '#212121' }}>로그인</Button>
            )}
          </Box>
        </Toolbar>

        {/* PC 서브 메뉴 (2번째 줄) */}
        {!isMobile && activeMenu?.submenu && (
          <Box sx={{ bgcolor: '#fafafa', borderTop: '1px solid #f0f0f0', px: 4, display: 'flex', gap: 2 }}>
            {activeMenu.submenu.map((sub) => (
              <Button
                key={sub.text}
                onClick={() => navigate(sub.path)}
                sx={{
                  color: location.pathname === sub.path ? '#212121' : '#757575',
                  fontWeight: location.pathname === sub.path ? 700 : 500,
                  fontSize: '0.85rem',
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'transparent',
                    color: '#212121'
                  }
                }}
              >
                {sub.text}
              </Button>
            ))}
          </Box>
        )}
      </AppBar>

      {/* 스마트폰 풀스크린 메뉴 (바텀 시트) */}
      <Drawer
        anchor="bottom"
        open={mobileOpen && isMobile}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { 
            height: '85vh', 
            borderRadius: '20px 20px 0 0', 
            p: 2, 
            bgcolor: 'background.default' 
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 1 }}>
          <Typography variant="h6" fontWeight="bold">전체 메뉴</Typography>
          <IconButton onClick={handleDrawerToggle}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ overflowY: 'auto', pb: 4 }}>
          {menuItems.map((item, idx) => (
            <Box key={idx} sx={{ mb: 4 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5, px: 1, fontWeight: 'bold' }}>{item.text}</Typography>
              <Grid container spacing={1.5}>
                {item.submenu ? item.submenu.map(sub => (
                  <Grid item xs={4} key={sub.text}>
                    <Card 
                      sx={{ 
                        textAlign: 'center', p: 1.5, borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        '&:active': { bgcolor: 'grey.100' }
                      }} 
                      onClick={() => { navigate(sub.path); setMobileOpen(false); }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 1, display: 'flex', justifyContent: 'center' }}>{sub.icon}</Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>{sub.text}</Typography>
                    </Card>
                  </Grid>
                )) : (
                  <Grid item xs={4}>
                    <Card 
                      sx={{ 
                        textAlign: 'center', p: 1.5, borderRadius: 3, boxShadow: 'none', border: '1px solid', borderColor: 'grey.200',
                        cursor: 'pointer',
                        '&:active': { bgcolor: 'grey.100' }
                      }} 
                      onClick={() => { navigate(item.path); setMobileOpen(false); }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 1, display: 'flex', justifyContent: 'center' }}>{item.icon}</Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>{item.text}</Typography>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </Box>
      </Drawer>

      {/* 메인 콘텐츠 */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          pt: { xs: '56px', sm: '64px' }, // AppBar 높이만큼 기본 상단 여백
          pb: isMobile ? '65px' : 0 // 모바일 하단 탭 바 높이 여백
        }}
      >
        {/* PC 서브메뉴가 있는 경우 추가 여백 확보 */}
        {!isMobile && activeMenu?.submenu && <Box sx={{ height: '48px' }} />}

        {/* 콘텐츠 영역 */}
        <Box sx={{ p: { xs: 1, sm: 4 }, flexGrow: 1 }}>
          {children}
        </Box>
      </Box>

      {/* 모바일 하단 탭 바 */}
      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }} elevation={4}>
          <BottomNavigation
            showLabels
            value={bottomNavValue}
            onChange={(event, newValue) => {
              if (newValue === 0) navigate('/');
              else if (newValue === 1) navigate('/mbe_test');
              else if (newValue === 2) navigate('/community');
              else if (newValue === 3) navigate('/my-learning');
              else if (newValue === 4) handleDrawerToggle(); // 전체 메뉴 바텀시트 열기
            }}
            sx={{ 
              height: 65,
              '& .MuiBottomNavigationAction-root': { minWidth: 'auto', padding: '6px 0' },
              '& .MuiBottomNavigationAction-label': { fontSize: '0.75rem', fontWeight: 500, mt: 0.5 },
              '& .Mui-selected': { fontWeight: 700 }
            }}
          >
            <BottomNavigationAction label="홈" icon={<HomeIcon />} />
            <BottomNavigationAction label="MBE" icon={<QuizIcon />} />
            <BottomNavigationAction label="커뮤니티" icon={<ForumIcon />} />
            <BottomNavigationAction label="마이" icon={<AccountIcon />} />
            <BottomNavigationAction label="전체" icon={<MenuIcon />} />
          </BottomNavigation>
        </Paper>
      )}

      {/* 사용자 메뉴 */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => { navigate('/settings/profile'); handleUserMenuClose(); }}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          프로필
        </MenuItem>
        <MenuItem onClick={() => { navigate('/settings'); handleUserMenuClose(); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          설정
        </MenuItem>
        <MenuItem onClick={() => { navigate('/pricing'); handleUserMenuClose(); }}>
          <ListItemIcon>
            <PaymentIcon fontSize="small" />
          </ListItemIcon>
          요금제
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          로그아웃
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ClienLayout; 